import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Cached access token — set by AuthContext, avoids calling getSession() on every request
let _accessToken = null;

export function setAccessToken(token) {
  _accessToken = token;
}

// Add cached JWT to all authenticated requests (no async getSession call)
API.interceptors.request.use((config) => {
  if (_accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// Auth
export const getMe = () => API.get('/auth/me');
export const logout = () => API.post('/auth/logout');

// User
export const getUsage = () => API.get('/users/usage');

// Measurements
export const createMeasurement = (data) => API.post('/measurements', data);
export const getMeasurements = () => API.get('/measurements');
export const getMeasurement = (id) => API.get(`/measurements/${id}`);
export const deleteMeasurement = (id) => API.delete(`/measurements/${id}`);

// Reports
export const generateReport = (measurementId, includeGutters = false) => API.post(`/reports/${measurementId}`, { include_gutters: includeGutters });
export const getReports = () => API.get('/reports');
export const getReport = (id) => API.get(`/reports/${id}`);
export const updateReport = (id, data) => API.put(`/reports/${id}`, data);
export const toggleReportGutters = (id, includeGutters) => API.put(`/reports/${id}`, { include_gutters: includeGutters });
export const recalculateMaterials = (id, body = {}) => API.post(`/reports/${id}/recalculate`, body);
export const downloadReportPdf = (id) => {
  return API.get(`/reports/${id}/pdf`, { responseType: 'blob' });
};
export const downloadReportCsv = (id) => {
  return API.get(`/reports/${id}/csv`, { responseType: 'blob' });
};
export const createShareLink = (id) => API.post(`/reports/${id}/share`);
export const getSharedReport = (token) => API.get(`/shared/${token}`);

// Material Costs
export const updateMaterialCosts = (id, costs, customCosts = {}, lineItems = []) =>
  API.put(`/reports/${id}/costs`, { costs, custom_costs: customCosts, line_items: lineItems });
export const getMaterialCosts = (id) => API.get(`/reports/${id}/costs`);

// Referrals
export const getReferralCode = () => API.get('/referral/code');
export const redeemReferral = (code) => API.post('/referral/redeem', { referral_code: code });

// Logo Upload
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/upload/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getFile = (path) => API.get(`/files/${path}`, { responseType: 'blob' });

// Multi-facet Measurements
export const createMultiFacetMeasurement = (data) => API.post('/measurements/multi-facet', data);

// User Settings
export const getUserSettings = () => API.get('/users/settings');
export const updateUserSettings = (data) => API.put('/users/settings', data);

// Team Management
export const inviteTeamMember = (email, role) => API.post('/team/invite', { email, role });
export const getTeamMembers = () => API.get('/team/members');
export const removeTeamMember = (memberId) => API.delete(`/team/member/${memberId}`);
export const cancelTeamInvite = (inviteId) => API.delete(`/team/invite/${inviteId}`);
export const acceptTeamInvite = (inviteToken) => API.post('/team/accept', { invite_token: inviteToken });

// Contact
export const sendContactMessage = (data) => API.post('/contact', data);

// Subscriptions
export const getSubscriptionTiers = () => API.get('/subscriptions/tiers');

// Trial activation (email "activate 7-day Contractor trial" link — public, token-based)
export const activateTrial = (token) => API.post('/activate-trial', { token });

// Payments
export const createCheckout = (productType, originUrl) =>
  API.post('/payments/checkout', { product_type: productType, origin_url: originUrl });
export const getPaymentStatus = (sessionId) => API.get(`/payments/status/${sessionId}`);
export const createCustomerPortal = (returnUrl) =>
  API.post('/payments/customer-portal', { return_url: returnUrl });

// Subscription management — reads live Stripe state so the cancel path stays
// visible even if the local subscription_tier string has drifted.
export const getSubscription = () => API.get('/payments/subscription');
export const cancelSubscription = (reason) =>
  API.post('/payments/cancel-subscription', { reason });
export const resumeSubscription = () => API.post('/payments/resume-subscription');

// Email preferences
export const getEmailPreferences = () => API.get('/user/email-preferences');
export const updateEmailPreferences = (emailOptOut) =>
  API.patch('/user/email-preferences', { email_opt_out: emailOptOut });
